import { InstagramFilled, TikTokFilled, YoutubeFilled } from "@ant-design/icons";
import { ROUTES } from "@app/constants";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import { PLATFORM_OPTIONS } from "@shared/lib/constants";
import { IBrief } from "@shared/lib/types/brief";
import { Button, Table, Tooltip } from "@shared/ui";
import { Avatar, Flex } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import { ExternalLink, FileText, UserCheck, Zap } from "lucide-react";
import { JSX, useMemo, useState } from "react";
import { useGetBriefFacets } from "../lib/hooks/useGetBriefFacets";
import { getStatusLabel } from "../lib/utils/briefHelpers";
import { getStatusColorClass } from "../lib/utils/statusHelpers";
import styles from "./CampaignTableView.module.scss";

interface CampaignTableViewProps {
  briefs: IBrief[];
  isFetching: boolean;
  pageNo: number;
  pageSize: number;
  totaldata: number;
  onPageChange: (page: number) => void;
  onRowClick: (brief: IBrief) => void;
  className?: string;
  title?: JSX.Element;
  onSelectedAccountManagerBrandIdsChange?: (accountManagersBrandIds: string[]) => void;
}

export const CampaignTableView = ({
  briefs,
  isFetching,
  pageNo,
  pageSize,
  totaldata,
  onPageChange,
  onRowClick,
  className,
  title,
  onSelectedAccountManagerBrandIdsChange
}: CampaignTableViewProps) => {
  const totalPages = Math.max(1, Math.ceil((totaldata || 0) / pageSize) || 1);
  const { userDetails } = useGetMe();
    const [accountManagers, setAccountManagers] = useState<string[]>([]);

      const { facets, isFetching: isFetchingFacets } = useGetBriefFacets();
    
      const accountManagersToDisplay =
    (facets?.accountManagers || []).map((item) => {
      return {
        value: item._id,
        label: item.user.firstName + " " + item.user.lastName,
      };
    }) || [];
    const handleAccountManagerChange = (value: string[]) => {
    if (value === undefined) {
      setAccountManagers([]);
      return;
    }
    setAccountManagers(value);
    const brandIdsToProcess =
      facets?.accountManagers
        ?.filter((item) => value.includes(item._id))
        .flatMap((item) => item.brandIds) || [];
    onSelectedAccountManagerBrandIdsChange?.(brandIdsToProcess);
  };
  const columns: ColumnsType<IBrief> = useMemo(
    () => [
      {
        title: "Campaign",
        dataIndex: "name",
        key: "name",
        width: "30%",
        render: (_value, record) => (
          <Flex gap={20}>
            <Avatar size={30} src={record?.brand?.logoUrl} alt={record?.brand?.name?.[0]} style={{ fontSize: 15,objectFit: "cover" }} />
            <Flex vertical gap={5}>
              <div
                className={styles.campaignLink}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();

                  let link = "";

                  if (record.mainStatus === "Campaign") {
                    link =
                      ROUTES.BRIEF_V2.replace(":briefId", record._id) +
                      "?tab=execution";
                  } else {
                    if (record?.totalInfluencerCount > 0) {
                      link =
                        ROUTES.BRIEF_V2.replace(":briefId", record._id) +
                        "?tab=brief";
                    } else {
                      link =
                        ROUTES.BRIEF_V2.replace(":briefId", record._id) +
                        "?tab=overview";
                    }
                  }

                  window.open(link, "_blank");
                }}
              >
                {record.name || "Untitled"}
                <ExternalLink
                  size={13}
                  strokeWidth={2.5}
                  className={styles.externalLinkIcon}
                />
              </div>
              <span>{record?.brand?.name}</span>
            </Flex>          
          </Flex>
        ),
      },
    //   {
    //   title: "Account Manager",
    //   dataIndex: "accountManager",
    //   key: "accountManager",
    //   filtered: accountManagers?.length > 0 ? true : false,
    //   filterDropdown: ({ confirm, close }) => (
    //     <SearchMultiSelectFilter
    //       confirm={confirm}
    //       close={close}
    //       searchPlaceholder="Search account managers"
    //       options={accountManagersToDisplay}
    //       selectedValues={accountManagers}
    //       onApply={handleAccountManagerChange}
    //     />
    //   ),
    //   render: (_: string, record: IBrief) => {
    //     const accountManagers =
    //       record?.accountManagers?.map(
    //         (item) => `${item?.firstName} ${item?.lastName}`
    //       ) || [];
    //     return <TextWithTooltip text={accountManagers.join(" ,")} />;
    //   },
    // },
      {
        title: "Status",
        dataIndex: "mainStatus",
        key: "status",
        render: (value, record) => {
          const status =
            record?.status === "Closed" ? "Closed" : record?.mainStatus;
          return (
            <span
              className={`${styles.statusBadge} ${getStatusColorClass(
                status,
                styles
              )}`}
            >
              {getStatusLabel(status)}
            </span>
          );
        },
      },
      {
        title: "Platform",
        dataIndex: "platform",
        key: "platform",
        render: (_value, record: IBrief) => {
          const platforms = record.form?.platform || [];

          const normalized = platforms.map((p: string) => p.toLowerCase());
          const knownPlatforms = ["instagram", "youtube", "tiktok"];
          const otherPlatforms = normalized.filter(
            (p) => !knownPlatforms.includes(p)
          );
          const getPlatformLabel = (val: string) =>
            PLATFORM_OPTIONS.find(
              (o) => o.value.toLowerCase() === val.toLowerCase()
            )?.label || val;

          if (!platforms.length) {
            return <span className={styles.platformFallback}>—</span>;
          }

          return (
            <div className={styles.platformContainer}>
              {normalized.includes("instagram") && (
                <div className={styles.platformIconInstagram} title="Instagram">
                  <InstagramFilled size={12} />
                </div>
              )}
              {normalized.includes("youtube") && (
                <div className={styles.platformIconYoutube} title="YouTube">
                  <YoutubeFilled size={12} color="currentColor" />
                </div>
              )}
              {normalized.includes("tiktok") && (
                <div className={styles.platformIconTiktok} title="TikTok">
                  {/* <span className={styles.tiktokLabel}>Tk</span> */}
                  <TikTokFilled size={12}/>
                </div>
              )}
              {otherPlatforms.length > 0 && (
                <Tooltip
                  title={otherPlatforms
                    .map((p) => getPlatformLabel(p))
                    .join(", ")}
                >
                  <span className={styles.platformOtherTag}>
                    +{otherPlatforms.length}
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: "Suggested",
        dataIndex: "discoveryCounts",
        key: "suggested",
        align: "center",
        render: (_value, record) => (
          <div className={styles.metricCell}>
            <FileText size={14} className={styles.metricIcon} />
            <span className={styles.metricValue}>
              {record.suggestedCount ?? 0}
            </span>
          </div>
        ),
      },
      {
        title: "Hired",
        dataIndex: "pricingCounts",
        key: "hired",
        align: "center",
        render: (_value, record) => (
          <div className={styles.metricCell}>
            <UserCheck size={14} className={styles.metricIcon} />
            <span className={styles.metricValue}>{record.hiredCount ?? 0}</span>
          </div>
        ),
      },
      {
        title: "Live",
        dataIndex: "purchasedCounts",
        key: "live",
        align: "center",
        render: (_value, record) => {
          const liveValue = record.liveCount ?? 0;
          const isLive = liveValue > 0;
          return (
            <div className={styles.metricCell}>
              <Zap
                size={14}
                className={isLive ? styles.metricIconLive : styles.metricIcon}
              />
              <span
                className={
                  isLive ? styles.metricValueLive : styles.metricValueInactive
                }
              >
                {liveValue}
              </span>
            </div>
          );
        },
      },
    ],
    [
      userDetails,
      accountManagersToDisplay,
      accountManagers
    ]
  );

  const dataSource = useMemo(
    () =>
      briefs.map((brief) => ({
        ...brief,
        key: brief._id,
      })),
    [briefs]
  );

  const handleTableChange: TableProps<IBrief>["onChange"] = (pagination) => {
    if (pagination?.current) {
      onPageChange(pagination.current);
    }
  };

  return (
    <Table<IBrief>
      wrapperClassName={className}
      columns={columns}
      title={() => title}
      dataSource={dataSource}
      loading={isFetching}
      pagination={{
        current: pageNo,
        pageSize,
        total: totaldata,
        showSizeChanger: false,
        // showTotal: (total) => (
        //   <span className={styles.paginationText}>
        //     Showing {total} campaigns
        //   </span>
        // ),
        className: styles.paginationBar,
        itemRender: (_page, type, originalElement) => {
          if (type === "prev") {
            return (
              <Button
                size="small"
                className={styles.paginationNav}
                disabled={pageNo <= 1}
                onClick={() => {
                  if (pageNo > 1) {
                    onPageChange(pageNo - 1);
                  }
                }}
              >
                Previous
              </Button>
            );
          }
          if (type === "next") {
            return (
              <Button
                size="small"
                disabled={pageNo >= totalPages}
                className={styles.paginationNav}
                onClick={() => {
                  if (pageNo < totalPages) {
                    onPageChange(pageNo + 1);
                  }
                }}
              >
                Next
              </Button>
            );
          }
          return originalElement;
        },
      }}
      onChange={handleTableChange}
      rowKey="_id"
      rowClassName={() => styles.tableRow}
      onRow={(record) => ({
        onClick: () => onRowClick(record as IBrief),
      })}
      scroll={{ x: true }}
      locale={{
        emptyText: (
          <div className={styles.emptyState}>
            No campaigns found matching your criteria.
          </div>
        ),
      }}
    />
  );
};
